import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, AlertCircle, Lightbulb, Loader2, ArrowRight, X, ChevronDown, ChevronUp } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * Renewal-pipeline insights — outcome-based prescriptive alerts. Renders a
 * stack of cards sorted critical → warning → info, each with severity icon,
 * title, evidence body, and a deep-link CTA.
 *
 * If `showAll` is false (default), shows the top 3 plus a toggle. Designed to
 * be embedded above the Counter-Offer Inbox on Leases or inside the Renewal
 * Pipeline card on Performance.
 *
 * Backed by `GET /api/lease-renewal-proposals/insights`.
 */
export default function RenewalInsights({ showAll = false, hideWhenEmpty = true }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(showAll);
  const [dismissed, setDismissed] = useState(() => readDismissed());

  useEffect(() => {
    let mounted = true;
    api.get("/lease-renewal-proposals/insights")
      .then((r) => { if (mounted) setData(r.data); })
      .catch((err) => {
        if (mounted) {
          toast.error(formatApiError(err.response?.data?.detail) || "Could not load insights");
        }
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  if (loading) return null;
  if (!data) return null;

  const items = (data.items || []).filter((i) => !dismissed.includes(i.id));
  if (items.length === 0) {
    if (hideWhenEmpty) return null;
    return (
      <div className="border border-emerald-200 bg-emerald-50/40 rounded-sm px-4 py-3 text-sm text-emerald-800 inline-flex items-center gap-2" data-testid="renewal-insights-empty">
        <Lightbulb className="w-4 h-4" />
        Renewal pipeline looks healthy — no actions needed right now.
      </div>
    );
  }

  const visible = expanded ? items : items.slice(0, 3);
  const remaining = items.length - visible.length;

  const dismiss = (id) => {
    const next = [...dismissed, id];
    setDismissed(next);
    saveDismissed(next);
  };

  return (
    <div className="space-y-2" data-testid="renewal-insights">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500" style={{ fontFamily: "Outfit" }}>
          Renewal insights
          <span className="ml-2 text-slate-400 normal-case tracking-normal">
            ({items.length} actionable)
          </span>
        </div>
        {dismissed.length > 0 ? (
          <button
            onClick={() => { setDismissed([]); saveDismissed([]); }}
            className="text-xs text-slate-500 hover:text-slate-900 underline"
            data-testid="renewal-insights-restore"
          >
            Restore {dismissed.length} dismissed
          </button>
        ) : null}
      </div>

      <div className="space-y-2" data-testid="renewal-insights-list">
        {visible.map((item) => (
          <InsightCard key={item.id} item={item} onDismiss={() => dismiss(item.id)} />
        ))}
      </div>

      {remaining > 0 ? (
        <button
          onClick={() => setExpanded(true)}
          className="text-xs font-semibold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
          data-testid="renewal-insights-show-more"
        >
          <ChevronDown className="w-3 h-3" /> Show {remaining} more
        </button>
      ) : expanded && items.length > 3 ? (
        <button
          onClick={() => setExpanded(false)}
          className="text-xs font-semibold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
          data-testid="renewal-insights-collapse"
        >
          <ChevronUp className="w-3 h-3" /> Show less
        </button>
      ) : null}
    </div>
  );
}

function InsightCard({ item, onDismiss }) {
  const cfg = SEVERITY_STYLES[item.severity] || SEVERITY_STYLES.info;
  const Icon = cfg.Icon;
  return (
    <div
      className={`border rounded-sm px-4 py-3 ${cfg.bg} ${cfg.border}`}
      data-testid={`insight-${item.id}`}
    >
      <div className="flex items-start gap-3">
        <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${cfg.iconColor}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className={`text-[10px] uppercase tracking-wider font-bold ${cfg.label}`}>
              {item.severity}
            </span>
            <span className="text-[10px] text-slate-500 font-mono">
              {item.category.replace(/_/g, " ")}
            </span>
          </div>
          <div className={`font-semibold mt-0.5 ${cfg.title}`} style={{ fontFamily: "Outfit" }}>
            {item.title}
          </div>
          <div className={`text-xs mt-1 leading-relaxed ${cfg.body}`}>
            {item.body}
          </div>
          <div className="mt-2 flex items-center gap-3">
            <Link
              to={item.cta_path}
              className={`text-xs font-semibold inline-flex items-center gap-1 ${cfg.cta}`}
              data-testid={`insight-cta-${item.id}`}
            >
              {item.cta_label}
              <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
        <button
          onClick={onDismiss}
          className="text-slate-400 hover:text-slate-700 p-1 -m-1 shrink-0"
          aria-label="Dismiss"
          data-testid={`insight-dismiss-${item.id}`}
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

const SEVERITY_STYLES = {
  critical: {
    Icon: AlertCircle,
    bg: "bg-rose-50",
    border: "border-rose-200",
    iconColor: "text-rose-600",
    label: "text-rose-700",
    title: "text-rose-950",
    body: "text-rose-900",
    cta: "text-rose-800 hover:text-rose-950",
  },
  warning: {
    Icon: AlertTriangle,
    bg: "bg-amber-50",
    border: "border-amber-200",
    iconColor: "text-amber-700",
    label: "text-amber-800",
    title: "text-amber-950",
    body: "text-amber-900",
    cta: "text-amber-900 hover:text-amber-950",
  },
  info: {
    Icon: Lightbulb,
    bg: "bg-sky-50",
    border: "border-sky-200",
    iconColor: "text-sky-700",
    label: "text-sky-800",
    title: "text-sky-950",
    body: "text-sky-900",
    cta: "text-sky-900 hover:text-sky-950",
  },
};

// --- Local "dismissed for this session" persistence -----------------------
const STORAGE_KEY = "rv_dismissed_renewal_insights";

function readDismissed() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveDismissed(ids) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // sessionStorage disabled — non-fatal
  }
}
